import React from "react";
import { Link } from "react-router-dom";
import { BookOpen, ArrowRight } from "lucide-react";
import MarketingLayout, { useMLang } from "./MarketingLayout";

const DOCS = {
    en: {
        title: "Get up and running in 30 minutes.",
        sub: "Everything you need to launch your restaurant on HeavenlyEats. No technical skills required.",
        sections: [
            {
                heading: "1. Sign up & create your tenant",
                body: "Pick a slug (e.g. `lacasita`) on the pricing page, pay the $299 setup fee, and your storefront will live at `yourplatform.com/r/lacasita`. The 14-day trial begins the moment your subscription activates.",
            },
            {
                heading: "2. Configure your brand",
                body: "Log into `/admin`, open the Configuración tab, and edit: brand name, brand initials, colors (primary + accent), country flag, hero headline (EN+ES), chef bio, WhatsApp number, Lynk/Tpago/PayPal numbers, currency, delivery fee, and free-delivery threshold. Save once and the whole site recolors instantly.",
            },
            {
                heading: "3. Add your menu",
                body: "From the Menu tab, click \"Add item\". For each dish: bilingual name/description, price, optional cost breakdown (for the Profitability dashboard), category, and an image. Pro tier: click \"Generate with AI\" to produce a professional photo via Gemini Nano Banana.",
            },
            {
                heading: "4. Take your first WhatsApp order",
                body: "Visit your storefront on your phone. Tap a dish, fill name + phone + pickup date, and tap \"Send via WhatsApp\". Your tenant WhatsApp number receives a perfectly-formatted message with items, total, payment details, and customer contact.",
            },
            {
                heading: "5. Daily operations",
                body: "Open the Orders tab each morning. Mark orders as `confirmed → preparing → ready → completed`. Use the Calendar tab to plan multi-day prep and broadcast pickup reminders. Archive completed orders weekly.",
            },
            {
                heading: "6. Renewals & support",
                body: "Pro tip: enable auto-pay through Stripe Billing Portal so you never lose service. If you're on a manual plan (Lynk/Tpago/cash), we'll email you 7 days before renewal. Email support at hello@heavenlyai.solutions for anything else.",
            },
        ],
        cta: "Ready to launch?",
        ctaBtn: "Start your 14-day trial",
    },
    es: {
        title: "Listo para vender en 30 minutos.",
        sub: "Todo lo que necesitas para lanzar tu restaurante en HeavenlyEats. No requiere conocimientos técnicos.",
        sections: [
            {
                heading: "1. Regístrate y crea tu tenant",
                body: "Elige un slug (ej. `lacasita`) en la página de precios, paga el setup de $299, y tu tienda quedará en `tuplataforma.com/r/lacasita`. La prueba de 14 días empieza al activar tu suscripción.",
            },
            {
                heading: "2. Configura tu marca",
                body: "Entra a `/admin`, abre la pestaña Configuración, y edita: nombre, iniciales, colores (primario + acento), bandera del país, titular del hero (EN+ES), bio del chef, número de WhatsApp, números de Lynk/Tpago/PayPal, moneda, costo de envío y umbral de envío gratis. Guarda una vez y todo el sitio se actualiza al instante.",
            },
            {
                heading: "3. Agrega tu menú",
                body: "Desde la pestaña Menú, haz click en \"Agregar plato\". Para cada plato: nombre/descripción bilingüe, precio, desglose de costos opcional (para el panel de rentabilidad), categoría e imagen. Plan Pro: \"Generar con IA\" produce una foto profesional con Gemini Nano Banana.",
            },
            {
                heading: "4. Tu primer pedido por WhatsApp",
                body: "Visita tu tienda desde el celular. Toca un plato, completa nombre + teléfono + fecha y toca \"Enviar por WhatsApp\". Tu número de WhatsApp recibe un mensaje perfecto con los items, total, datos de pago y contacto del cliente.",
            },
            {
                heading: "5. Operación diaria",
                body: "Abre la pestaña Pedidos cada mañana. Marca pedidos como `confirmado → preparando → listo → completado`. Usa la pestaña Calendario para planear varios días y enviar recordatorios masivos. Archiva pedidos cada semana.",
            },
            {
                heading: "6. Renovaciones y soporte",
                body: "Consejo: activa pago automático en el portal de Stripe para no perder servicio. Si pagas manualmente (Lynk/Tpago/efectivo), te enviaremos un correo 7 días antes de la renovación. Para todo lo demás: hello@heavenlyai.solutions.",
            },
        ],
        cta: "¿Listo para lanzar?",
        ctaBtn: "Empieza tu prueba de 14 días",
    },
};

const MarketingDocs = () => {
    const { lang } = useMLang();
    const c = DOCS[lang];
    return (
        <MarketingLayout testid="marketing-docs">
            <section className="max-w-3xl mx-auto px-6 sm:px-10 pt-16 sm:pt-24 pb-12">
                <div className="flex items-center gap-3 text-xs uppercase tracking-[0.18em] text-orange-300 mb-4">
                    <BookOpen className="w-4 h-4" /> {lang === "es" ? "Guía de inicio" : "Quick-start guide"}
                </div>
                <h1 className="font-serif font-black text-4xl sm:text-5xl tracking-tight">{c.title}</h1>
                <p className="text-lg text-white/65 mt-5">{c.sub}</p>
            </section>

            <section className="max-w-3xl mx-auto px-6 sm:px-10 pb-16 space-y-4">
                {c.sections.map((s, i) => (
                    <div
                        key={i}
                        data-testid={`docs-section-${i}`}
                        className="p-6 rounded-2xl border border-white/10 bg-white/[0.03]"
                    >
                        <h2 className="font-serif font-bold text-xl mb-3">{s.heading}</h2>
                        <p className="text-sm text-white/70 leading-relaxed whitespace-pre-wrap">{s.body}</p>
                    </div>
                ))}
            </section>

            <section className="max-w-3xl mx-auto px-6 sm:px-10 pb-24">
                <div className="rounded-3xl border border-orange-400/30 bg-orange-500/[0.07] p-8 flex flex-col sm:flex-row gap-5 items-start sm:items-center justify-between">
                    <h3 className="font-serif font-bold text-2xl">{c.cta}</h3>
                    <Link to="/heavenly/signup" data-testid="docs-cta" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-orange-500 hover:bg-orange-400 text-white font-semibold">
                        {c.ctaBtn}
                        <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>
        </MarketingLayout>
    );
};

export default MarketingDocs;
